/**
 * errorInterceptor.ts - 고급 에러 처리 인터셉터
 * 
 * API 에러에 대한 체계적인 처리와 사용자 친화적 메시지를 제공합니다.
 */

import { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

// 에러 타입 정의
export enum ErrorType {
  NETWORK = 'NETWORK',
  TIMEOUT = 'TIMEOUT',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  AUTH = 'AUTH',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
}

// 에러 세부 정보
export interface ErrorDetails {
  type: ErrorType;
  code: string;
  message: string;
  userMessage: string;
  originalError: AxiosError;
  timestamp: number;
  requestId?: string;
  retryable: boolean;
}

// 에러 핸들러 인터페이스
export interface ErrorHandler {
  canHandle: (error: AxiosError) => boolean;
  handle: (error: AxiosError) => ErrorDetails;
}

// 네트워크 에러 핸들러
export const networkErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => !error.response,
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.NETWORK,
    code: 'NETWORK_ERROR',
    message: error.message,
    userMessage: '네트워크 연결을 확인해주세요.',
    originalError: error,
    timestamp: Date.now(),
    retryable: true,
  }),
};

// 타임아웃 에러 핸들러
export const timeoutErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => error.code === 'ECONNABORTED' || error.message.includes('timeout'),
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.TIMEOUT,
    code: 'TIMEOUT_ERROR',
    message: error.message,
    userMessage: '요청 시간이 초과되었습니다. 다시 시도해주세요.',
    originalError: error,
    timestamp: Date.now(),
    retryable: true,
  }),
};

// 인증 에러 핸들러
export const authErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => error.response?.status === 401,
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.AUTH,
    code: 'UNAUTHORIZED',
    message: (error.response?.data as any)?.message || 'Unauthorized',
    userMessage: '로그인이 필요합니다.',
    originalError: error,
    timestamp: Date.now(),
    requestId: error.response?.headers?.['x-request-id'],
    retryable: false,
  }),
};

// 권한 에러 핸들러
export const forbiddenErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => error.response?.status === 403,
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.FORBIDDEN,
    code: 'FORBIDDEN',
    message: (error.response?.data as any)?.message || 'Forbidden',
    userMessage: '접근 권한이 없습니다.',
    originalError: error,
    timestamp: Date.now(),
    requestId: error.response?.headers?.['x-request-id'],
    retryable: false,
  }),
};

// 404 에러 핸들러
export const notFoundErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => error.response?.status === 404,
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.NOT_FOUND,
    code: 'NOT_FOUND',
    message: (error.response?.data as any)?.message || 'Not Found',
    userMessage: '요청한 리소스를 찾을 수 없습니다.',
    originalError: error,
    timestamp: Date.now(),
    requestId: error.response?.headers?.['x-request-id'],
    retryable: false,
  }),
};

// 유효성 검사 에러 핸들러
export const validationErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => error.response?.status === 422 || error.response?.status === 400,
  handle: (error: AxiosError): ErrorDetails => {
    const responseData = error.response?.data as any;
    let userMessage = '입력 데이터를 확인해주세요.';
    
    // 유효성 검사 에러 메시지 파싱
    if (responseData?.errors) {
      const errorMessages = Object.values(responseData.errors).flat();
      userMessage = (errorMessages as string[]).join(', ');
    } else if (responseData?.detail) {
      userMessage = responseData.detail;
    }

    return {
      type: ErrorType.VALIDATION,
      code: 'VALIDATION_ERROR',
      message: (responseData as any)?.message || 'Validation Error',
      userMessage,
      originalError: error,
      timestamp: Date.now(),
      requestId: error.response?.headers?.['x-request-id'],
      retryable: false,
    };
  },
};

// 서버 에러 핸들러
export const serverErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => {
    const status = error.response?.status;
    return status ? status >= 500 && status < 600 : false;
  },
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.SERVER,
    code: 'SERVER_ERROR',
    message: (error.response?.data as any)?.message || 'Internal Server Error',
    userMessage: '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.',
    originalError: error,
    timestamp: Date.now(),
    requestId: error.response?.headers?.['x-request-id'],
    retryable: true,
  }),
};

// 클라이언트 에러 핸들러
export const clientErrorHandler: ErrorHandler = {
  canHandle: (error: AxiosError) => {
    const status = error.response?.status;
    return status ? status >= 400 && status < 500 : false;
  },
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.CLIENT,
    code: 'CLIENT_ERROR',
    message: (error.response?.data as any)?.message || 'Client Error',
    userMessage: '요청 처리 중 오류가 발생했습니다.',
    originalError: error,
    timestamp: Date.now(),
    requestId: error.response?.headers?.['x-request-id'],
    retryable: false,
  }),
};

// 기본 에러 핸들러
export const unknownErrorHandler: ErrorHandler = {
  canHandle: () => true,
  handle: (error: AxiosError): ErrorDetails => ({
    type: ErrorType.UNKNOWN,
    code: 'UNKNOWN_ERROR',
    message: error.message || 'Unknown Error',
    userMessage: '알 수 없는 오류가 발생했습니다.',
    originalError: error,
    timestamp: Date.now(),
    retryable: false,
  }),
};

// 기본 에러 핸들러 체인
export const defaultErrorHandlers: ErrorHandler[] = [
  networkErrorHandler,
  timeoutErrorHandler,
  authErrorHandler,
  forbiddenErrorHandler,
  notFoundErrorHandler,
  validationErrorHandler,
  serverErrorHandler,
  clientErrorHandler,
  unknownErrorHandler, // 마지막에 위치
];

// 에러 처리 옵션
export interface ErrorInterceptorOptions {
  handlers?: ErrorHandler[];
  onError?: (errorDetails: ErrorDetails) => void;
  reportError?: (errorDetails: ErrorDetails) => void;
  showUserMessage?: (message: string, type: ErrorType) => void;
}

// 에러 처리 인터셉터 설정
export const setupErrorInterceptor = (
  axiosInstance: AxiosInstance,
  options: ErrorInterceptorOptions = {}
): void => {
  const { 
    handlers = defaultErrorHandlers,
    onError,
    reportError,
    showUserMessage,
  } = options;

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      // 적절한 핸들러 찾기
      const handler = handlers.find(h => h.canHandle(error));
      
      if (!handler) {
        return Promise.reject(error);
      }

      // 에러 세부 정보 생성
      const errorDetails = handler.handle(error);

      // 콜백 실행
      if (onError) {
        onError(errorDetails);
      }

      // 에러 리포팅
      if (reportError && errorDetails.type !== ErrorType.VALIDATION) {
        reportError(errorDetails);
      }

      // 사용자 메시지 표시
      if (showUserMessage) {
        showUserMessage(errorDetails.userMessage, errorDetails.type);
      }

      // Enhanced error 객체 생성
      const enhancedError = Object.assign(error, {
        errorDetails,
        isRetryable: errorDetails.retryable,
      });

      return Promise.reject(enhancedError);
    }
  );
};

// 에러 상세 정보 추출 유틸리티
export const getErrorDetails = (error: any): ErrorDetails | null => {
  return error?.errorDetails || null;
};

// 재시도 가능 여부 확인 유틸리티
export const isRetryableError = (error: any): boolean => {
  return error?.isRetryable || false;
};